import React, { useContext, useMemo } from "react";
import InternalDebuggerElementView from "./InternalDebuggerElementView";
import { NodeGraphSolveContext } from "./InternalDebuggerNodeGraph";
import InternalDebuggerRelationshipView from "./InternalDebuggerRelationshipView";
import InternalDebuggerStateValueView from "./InternalDebuggerStateValueView";
import "./InternalDebuggerNodeInfoPanel.scss";
import type { GraphNode } from "@/pages/internal-debugger/InternalDebuggerPage";

export interface DebuggerNodeInfoPanelProps {
  graph?: any;
}

export interface NodeWithState extends GraphNode {
  v: string;
  value: any;
}

export const SelectedNodeContext = React.createContext<{
  selected: string | undefined;
  setSelected: (id: string | undefined) => void;
}>({
  selected: undefined,
  setSelected: () => {},
});

const InternalDebuggerNodeInfoPanel = (props: DebuggerNodeInfoPanelProps) => {
  const { graph } = props;

  const solve = useContext(NodeGraphSolveContext);
  const { selected } = useContext(SelectedNodeContext);

  const selectedNodes = useMemo<NodeWithState[] | undefined>(() => {
    const nodes =
      selected &&
      // @ts-ignore
      (solve?.graph.nodes.filter((node) => node.v.endsWith(selected) && !node.value.hidden) ||
        graph?.rule_graph.nodes.find((node) => node.v.endsWith(selected)));
    if (nodes?.length) {
      return nodes;
    }
    return undefined;
  }, [selected, solve, graph]);

  const selectedNode = selectedNodes?.[0];

  if (!selected) return null;

  return (
    <div className="NodeInfoPanel">
      {selectedNode && (
        <>
          <h3>
            {selectedNode.value.description}
            {selectedNode.value.identifier ? <span> (identifier)</span> : null}
          </h3>
          <div>{selected}</div>

          <br />

          <b>Entity</b>
          <div>{selectedNode.value.entity}</div>

          <br />
          <b>Type</b>
          <div>{selectedNode.value.type}</div>

          <br />

          {selectedNode.value.inferred && (
            <>
              <b>Inferred:</b>

              <div className="NodeInfoPanel__inferred">
                {selectedNode.value.inferred.rel && (
                  <>
                    <b>Relationship</b>
                    <section>
                      <InternalDebuggerRelationshipView
                        current={true}
                        id={selectedNode.value.inferred.rel}
                        relationships={graph!.relationships}
                      />
                    </section>
                    <br />
                  </>
                )}
                {selectedNode.value.inferred.conditions?.length > 0 && (
                  <>
                    <b>Condition</b>
                    <section>
                      {selectedNode.value.inferred.conditions.map((condition, index) => (
                        <InternalDebuggerElementView
                          release={graph}
                          graph={solve?.graph}
                          key={index}
                          value={condition}
                          depth={0}
                        />
                      ))}
                    </section>
                    <br />
                  </>
                )}
                {selectedNode.value.inferred.initial && (
                  <>
                    <b>Initial</b>
                    <section>
                      <InternalDebuggerElementView
                        release={graph}
                        graph={solve?.graph}
                        value={selectedNode.value.inferred.initial}
                        depth={0}
                      />
                    </section>
                  </>
                )}
              </div>
              <br />
            </>
          )}

          {selectedNode.value.value && (
            <>
              <b>Value</b>
              <section>
                {Array.isArray(selectedNode.value.value) ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Conditions</th>
                        <th>Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedNode.value.value.map((value, index) => {
                        return (
                          <tr key={index}>
                            <td>{index}</td>
                            <td>
                              <InternalDebuggerElementView
                                release={graph}
                                graph={solve?.graph}
                                value={selectedNode.value.rows[index][0]}
                              />
                            </td>
                            <td>
                              <InternalDebuggerElementView
                                release={graph}
                                graph={solve?.graph}
                                value={value}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <InternalDebuggerElementView
                    release={graph}
                    graph={solve?.graph}
                    value={selectedNode.value.value}
                  />
                )}
              </section>
              <br />
            </>
          )}
          {selectedNode.value.otherwise && (
            <>
              <b>Otherwise</b>
              <section>
                <InternalDebuggerElementView
                  release={graph}
                  graph={solve?.graph}
                  value={selectedNode.value.otherwise}
                />
              </section>
              <br />
            </>
          )}
          {selectedNode.value.conditions?.length > 0 && (
            <>
              <b>Condition</b>
              <section>
                {selectedNode.value.conditions.map((condition, index) => (
                  <InternalDebuggerElementView
                    release={graph}
                    key={index}
                    graph={solve?.graph}
                    value={condition}
                  />
                ))}
              </section>
              <br />
            </>
          )}
          {selectedNodes
            ? selectedNodes.map((node) => {
                return (
                  <React.Fragment key={node.v}>
                    <b>State ({node.v})</b>

                    <InternalDebuggerStateValueView state={node} />
                    <br />
                  </React.Fragment>
                );
              })
            : null}
        </>
      )}
      <textarea
        className="NodeInfoPanel__raw"
        readOnly={true}
        value={JSON.stringify(selectedNode, null, 2) || ""}
      />
    </div>
  );
};

export default InternalDebuggerNodeInfoPanel;
